<template>
  <div class="mb-10">
    <div class="flex items-center gap-3 mb-4">
      <span
        class="px-3 py-1 rounded-full text-xs font-semibold uppercase"
        :class="
          entity.type === 'groupe'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-purple-100 text-purple-700'
        "
      >
        {{
          entity.type === "groupe"
            ? $t("user_progression.stats.group")
            : $t("user_progression.stats.service")
        }}
      </span>
      <h2 class="text-lg font-bold">{{ entity.name }}</h2>
      <span v-if="entity.responsible" class="text-sm text-gray-500">
        {{ $t("user_progression.stats.responsible") }} :
        {{ entity.responsible }}
      </span>
    </div>

    <div
      :class="[
        'grid grid-cols-2 gap-4 mb-6',
        hasPluzz
          ? 'sm:grid-cols-3 md:grid-cols-5'
          : 'sm:grid-cols-2 md:grid-cols-4',
      ]"
    >
      <div
        v-for="(item, index) in kpiList"
        :key="index"
        class="bg-white p-4 rounded-xl shadow-md flex flex-col items-center justify-center"
      >
        <div class="relative w-24 h-24 flex items-center justify-center">
          <canvas
            :ref="(el) => setCanvasRef(el, index)"
            width="96"
            height="96"
            class="w-24 h-24"
          ></canvas>
          <div class="absolute inset-0 flex items-center justify-center">
            <span class="text-base font-bold text-gray-800">{{
              item.value
            }}</span>
          </div>
        </div>
        <div class="mt-2 text-center text-xs font-medium text-gray-600">
          {{ item.label }}
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
      <!-- Utilisateurs ayant commencé -->
      <StatsDoughnutChart
        :title="$t('user_progression.stats.users_started')"
        :labels="[
          $t('user_progression.stats.users_started_short'),
          $t('user_progression.stats.remaining'),
        ]"
        :data="buildSingleData(entity.users_started)"
        :colors="['#66BB6A', '#E0E0E0']"
      />

      <!-- Utilisateurs > 50% -->
      <StatsDoughnutChart
        :title="$t('user_progression.stats.users_over_50')"
        :labels="[
          $t('user_progression.stats.users_over_50_short'),
          $t('user_progression.stats.remaining'),
        ]"
        :data="buildSingleData(entity.users_over_50)"
        :colors="['#FFA726', '#E0E0E0']"
      />

      <!-- Utilisateurs ayant terminé -->
      <StatsDoughnutChart
        :title="$t('user_progression.stats.users_completed')"
        :labels="[
          $t('user_progression.stats.users_completed_short'),
          $t('user_progression.stats.remaining'),
        ]"
        :data="buildSingleData(entity.users_completed)"
        :colors="['#EF5350', '#E0E0E0']"
      />

      <!-- Certification -->
      <StatsDoughnutChart
        :title="$t('user_progression.stats.certification_status')"
        :labels="certificationLabels"
        :data="certificationData"
        :colors="['#26C6DA', '#E0E0E0']"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, defineProps } from "vue";
import { useI18n } from "vue-i18n";
import StatsDoughnutChart from "./StatsDoughnutChart.vue";

const { t } = useI18n();

const props = defineProps({
  entity: {
    type: Object,
    required: true,
  },
});

const hasPluzz = computed(() => {
  return (
    props.entity.progression_pluzz !== null && props.entity.progression_pluzz > 0
  );
});

const kpiList = computed(() => {
  const e = props.entity;
  const total = e.total_users ?? 0;

  const list = [
    {
      label: t("user_progression.stats.total_users"),
      value: e.total_users,
      percentage: 100,
      color: "#42A5F5",
    },
    {
      label: t("user_progression.stats.active_users"),
      value: e.active_users,
      percentage: Math.round((e.active_users / total) * 100),
      color: "#66BB6A",
    },
    {
      label: t("user_progression.stats.cyberscore"),
      value: e.cyberscore + "%",
      percentage: e.cyberscore,
      color: "#AB47BC",
    },
    {
      label: t("user_progression.stats.progression"),
      value: e.progression + "%",
      percentage: e.progression,
      color: "#FFA726",
    },
  ];

  if (hasPluzz.value) {
    list.push({
      label: t("user_progression.stats.progression_pluzz"),
      value: e.progression_pluzz + "%",
      percentage: e.progression_pluzz,
      color: "#26C6DA",
    });
  }

  return list;
});

const buildSingleData = (value) => {
  const count = Number(value) || 0;
  const total = Number(props.entity.total_users) || 0;
  const remaining = Math.max(total - count, 0);
  return [count, remaining];
};

const certificationLabels = computed(() => [
  t("user_progression.stats.certified"),
  t("user_progression.stats.not_certified"),
]);

const certificationData = computed(() => {
  const certified = Number(props.entity.certified_users) || 0;
  const notCertified = Math.max(
    (Number(props.entity.total_users) || 0) - certified,
    0,
  );
  return [certified, notCertified];
});

// Canvas circular progress
const canvasRefs = ref([]);

const setCanvasRef = (el, index) => {
  if (el) {
    canvasRefs.value[index] = el;
    drawCircularProgress(el, kpiList.value[index]);
  }
};

const drawCircularProgress = (canvas, data) => {
  const ctx = canvas.getContext("2d");
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const radius = 35;
  const lineWidth = 6;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
  ctx.strokeStyle = "#F3F4F6";
  ctx.lineWidth = lineWidth;
  ctx.stroke();

  // Progress arc
  const startAngle = -Math.PI / 2;
  const endAngle = startAngle + (2 * Math.PI * data.percentage) / 100;

  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, startAngle, endAngle);
  ctx.strokeStyle = data.color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  ctx.stroke();
};

watch(
  kpiList,
  (newData) => {
    newData.forEach((item, index) => {
      if (canvasRefs.value[index]) {
        drawCircularProgress(canvasRefs.value[index], item);
      }
    });
  },
  { deep: true },
);

onMounted(() => {
  setTimeout(() => {
    kpiList.value.forEach((item, index) => {
      if (canvasRefs.value[index]) {
        drawCircularProgress(canvasRefs.value[index], item);
      }
    });
  }, 100);
});
</script>
